/** How long after a wake abort to skip the slow-hub auto ticket. */
const TAB_WAKE_AUTO_REPORT_SUPPRESS_MS = 15_000;

export type TabWakeStaleFetch = {
  startedAtMs: number;
  headerDeadlineMs: number;
  hasHeaders: () => boolean;
  abort: () => void;
};

const inflight = new Set<TabWakeStaleFetch>();
let suppressAutoReportUntilMs = 0;
let listening = false;

export function registerTabWakeStaleFetch(entry: TabWakeStaleFetch): () => void {
  ensureTabWakeListener();
  inflight.add(entry);
  return () => {
    inflight.delete(entry);
  };
}

/** True briefly after a sleeping tab dropped calls that never got a reply. */
export function tabWakeSuppressesSlowHubAutoReport(nowMs: number): boolean {
  return nowMs < suppressAutoReportUntilMs;
}

/**
 * Drop in-flight calls that are already past the header deadline and have no
 * response bytes. Chrome does not run their timers while the tab is frozen.
 */
export function abortStaleFetchesAfterTabWake(nowMs = Date.now()): number {
  let aborted = 0;
  for (const entry of inflight) {
    if (entry.hasHeaders()) continue;
    const deadlineMs = entry.headerDeadlineMs > 0 ? entry.headerDeadlineMs : 10_000;
    if (nowMs - entry.startedAtMs < deadlineMs) continue;
    aborted += 1;
    entry.abort();
  }
  if (aborted > 0) {
    suppressAutoReportUntilMs = nowMs + TAB_WAKE_AUTO_REPORT_SUPPRESS_MS;
  }
  return aborted;
}

export function resetTabWakeStaleFetchForTests(): void {
  inflight.clear();
  suppressAutoReportUntilMs = 0;
}

function ensureTabWakeListener(): void {
  if (listening || typeof document === "undefined") return;
  listening = true;
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") abortStaleFetchesAfterTabWake();
  });
  if (typeof window !== "undefined") {
    window.addEventListener("online", () => abortStaleFetchesAfterTabWake());
  }
}
